import type { RateableServices, RateProfile, RateProfileHistoryEntry } from "@shyne/shared/api";
import { api } from "@/lib/api";

export type RatePayee = { worker: string } | { subcontractor: string };

export type RateSubject = { payer: string; payee: RatePayee };

export type ProfileRate = { rate: number | null; source: "profile" | "none" };

const payeeQuery = (payee: RatePayee) => ("worker" in payee ? { worker: payee.worker } : { subcontractor: payee.subcontractor });

const rateMapKey = (payee: RatePayee, service: string): string =>
  "worker" in payee ? `user:${payee.worker}:${service}` : `org:${payee.subcontractor}:${service}`;

const payeeOfProfile = (profile: RateProfile): RatePayee | null =>
  profile.payee.worker ? { worker: profile.payee.worker.id } : profile.payee.subcontractor ? { subcontractor: profile.payee.subcontractor.id } : null;

export const DailyWorkRateProfileRepository = {
  async listProfiles({ payer, payee }: RateSubject): Promise<RateProfile[]> {
    return (await api.rateProfiles.list({ payer, ...payeeQuery(payee) })).items;
  },

  async listHistory({ payer, payee }: RateSubject): Promise<RateProfileHistoryEntry[]> {
    return (await api.rateProfiles.history({ payer, ...payeeQuery(payee) })).items;
  },

  async listServicesForWorker(payer: string, worker: string): Promise<RateableServices["items"]> {
    return (await api.rateProfiles.services({ payer, worker })).items;
  },

  async saveRate({ payer, payee, service, hourlyRate }: RateSubject & { service: string; hourlyRate: number | null }): Promise<void> {
    await api.rateProfiles.set({ payer, payee, service, hourlyRate });
  },

  async profileRate({ payer, payee, service }: RateSubject & { service: string }): Promise<ProfileRate> {
    const [profile] = (await api.rateProfiles.list({ payer, service, ...payeeQuery(payee) })).items;
    return profile?.hourlyRate == null ? { rate: null, source: "none" } : { rate: profile.hourlyRate, source: "profile" };
  },

  async rateMapForPayer(payer: string): Promise<Record<string, number>> {
    const { items } = await api.rateProfiles.list({ payer });
    return Object.fromEntries(
      items.flatMap((profile) => {
        const payee = payeeOfProfile(profile);
        return payee === null || profile.hourlyRate === null ? [] : [[rateMapKey(payee, profile.service.id), profile.hourlyRate]];
      })
    );
  },
};
